import { TextFieldWithCheckbox } from "@/controls/form-fields/CheckboxField";
import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import { LostStrandType } from "@/pages/caster/models/caster.models";
import {
	deleteLostStrandType,
	upsertLostStrandType,
} from "@/pages/caster/repositories/ProductionRepository";
import { useModalStyles } from "@/pages/laboratory/styles/modalStyles";
import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import {
	Button,
	CircularProgress,
	Grid2 as Grid,
	Snackbar,
	SnackbarCloseReason,
	TextField,
} from "@mui/material";
import { Alert } from "@mui/material";
import React, { SyntheticEvent, useEffect, useMemo, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

type Props = {
	show: "create" | "edit" | "delete" | null;
	onHide: () => void;
	lostStrandType?: LostStrandType | null;
};
export const UpsertLostStrandType = ({
	show,
	onHide,
	lostStrandType,
}: Props) => {
	const classes = useModalStyles();
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [open, setOpen] = useState(false);
	const [error, setError] = useState("");
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const [snackbarMessage, setSnackbarMessage] = useState("");
	const {
		register,
		control,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<LostStrandType>({
		defaultValues: {
			LostStrandTypeId: 0,
			Description: "",
			IsActive: true,
		},
	});

	const title = useMemo(() => {
		if (show === "edit") {
			return "EDIT LOST STRAND TYPE";
		} else if (show === "delete") {
			return "DELETE LOST STRAND TYPE";
		} else if (show === "create") {
			return "NEW LOST STRAND TYPE";
		}
	}, [show]);
	const handleClose = (
		event: Event | SyntheticEvent<Element, Event>,
		reason?: SnackbarCloseReason,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setOpen(false);
	};
	const deleteLostStrantTypeAction = async (value?: string) => {
		if (value !== "OK") {
			setOpenDeleteDialog(false);
			return;
		}
		setOpenDeleteDialog(false);
		setIsSubmitLoading(true);
		const response = await deleteLostStrandType(
			lostStrandType?.LostStrandTypeId || 0,
		);
		if (response.ok) {
			setSnackbarMessage("The element was deleted successfully");
			setOpen(true);
			onHide();
		} else {
			setError(response.message);
		}
		setIsSubmitLoading(false);
	};
	const onSubmit: SubmitHandler<LostStrandType> = async (data: any) => {
		setIsSubmitLoading(true);
		if (show === "delete") {
			setOpenDeleteDialog(true);
		} else {
			const response = await upsertLostStrandType(data);
			if (response.ok) {
				setIsSubmitLoading(false);
				setSnackbarMessage(
					show === "edit"
						? "The element was updated successfully"
						: "The element was created successfully",
				);
				setOpen(true);
				onHide();
			} else {
				setError(response.message);
			}
		}

		setIsSubmitLoading(false);
	};
	useEffect(() => {
		if (show === "edit" || show === "create") {
			if (lostStrandType) {
				reset(lostStrandType);
			} else if (show === "create") {
				reset({
					LostStrandTypeId: 0,
					Description: "",
					IsActive: true,
				});
			}
		}
	}, [show, lostStrandType, reset]);
	return (
		<>
			<MesfModal
				open={!!show}
				handleClose={onHide}
				maxWidth="md"
				aria-labelledby="contained-modal-title-vcenter"
				title={title}
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content
						dividers
						className={classes.modalBody}
						style={{ padding: "15px 30px" }}
					>
						<Grid container spacing={1}>
							<Grid size={{ xs: 12, md: 12 }}>
								<TextField
									{...register("Description", { required: true })}
									label="Description"
									variant="outlined"
									error={!!errors.Description}
									fullWidth
									margin="dense"
									autoComplete="off"
									disabled={show === "delete"}
								/>
							</Grid>

							<Grid size={{ xs: 12, md: 6 }}>
								<Controller
									name="IsActive"
									control={control}
									render={({ field }) => (
										<TextFieldWithCheckbox
											checkboxValue={!!field.value}
											onCheckboxChange={field.onChange}
											label="Enabled"
											className={classes.checkbox}
											disabled={show === "delete"}
										/>
									)}
								/>
							</Grid>
						</Grid>
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
						<Grid container spacing={2} justifyContent="flex-end">
							<Grid size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
								<Button
									fullWidth
									variant="contained"
									color="inherit"
									onClick={onHide}
								>
									Cancel
								</Button>
							</Grid>
							<Grid size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
								<Button
									fullWidth
									startIcon={
										isSubmitLoading && <CircularProgress size="1rem" />
									}
									disabled={isSubmitLoading}
									variant="contained"
									color={show === "delete" ? "error" : "primary"}
									type="submit"
								>
									{show === "delete" ? "Delete" : "Save"}
								</Button>
							</Grid>
						</Grid>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<ConfirmationDialogRaw
				title="Confirmation"
				message={`Are you sure you want to delete permanently the lost strand type: ${lostStrandType?.Description}?`}
				open={openDeleteDialog}
				onClose={deleteLostStrantTypeAction}
			/>
			<Snackbar open={open} autoHideDuration={2500} onClose={handleClose}>
				<Alert
					elevation={6}
					variant="filled"
					severity="success"
					onClose={handleClose}
				>
					{snackbarMessage}
				</Alert>
			</Snackbar>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
